import { useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';

import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import FormHeading from 'common/FormHeading';
import { useRecoilState } from 'recoil';
import { businessData as businessDataAtom } from 'atoms';
import { calculateFees } from 'utils/calculateFees';
import { toasterNotification } from 'utils/toaster';
import { useAuth } from 'context/AuthContext';

const ManualPaymentForm = forwardRef(({
    invoiceId,
    isMakingPayment,
    setIsMakingPayment,
    onPaymentConfirmed,
    isAuthorized,
    patientName,
    onAuthorizationChange,
    patientId
}, ref) => {
    const { token } = useAuth();
    const stripeJs = useStripe();
    const elements = useElements();

    // Track completion for each Stripe field separately
    const [cardNumberComplete, setCardNumberComplete] = useState(false);
    const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
    const [cardCvcComplete, setCardCvcComplete] = useState(false);
    const [cardError, setCardError] = useState('');
    
    // State for prepopulated card data
    const [prepopulatedCardData, setPrepopulatedCardData] = useState(null);
    const [isLoadingCardData, setIsLoadingCardData] = useState(false);
    const [isEditingCard, setIsEditingCard] = useState(false);
    const [business] = useRecoilState(businessDataAtom);
    const isCardComplete = (prepopulatedCardData && !isEditingCard) || (cardNumberComplete && cardExpiryComplete && cardCvcComplete);

    // Expose state and handlers to parent component  
    const handlePayRef = useRef();
    
    useEffect(() => {
        handlePayRef.current = async () => {
          
          if (!stripeJs) {
            console.error('ManualPaymentForm: Stripe not loaded');
            setCardError('Stripe is not loaded. Please wait and try again.');
            return;
          }
          
          if (!isAuthorized) {
            console.error('ManualPaymentForm: Payment not authorized');
            setCardError('Please authorize the payment by checking the box.');
            return;
          }
      
          try {
            setIsMakingPayment(true);
            setCardError(''); // Clear any previous errors
            
            if (!token) {
              console.error('ManualPaymentForm: No authentication token found');
              setCardError('Authentication error. Please try again.');
              setIsMakingPayment(false);
              return;
            }
      
            // 1️⃣ Fetch invoice from backend API
            const invoiceResponse = await fetch(
              `${process.env.REACT_APP_SERVER_URL}/api/providers/invoice/${invoiceId}`,
              {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
              }
            );

            if (!invoiceResponse.ok) {
              throw new Error("Invoice not found");
            }

            const invoiceResult = await invoiceResponse.json();
            // Backend may return { success, data } or the data directly
            const invData = invoiceResult?.success ? invoiceResult.data[0]:null
             
            
            if (!invData) {
              throw new Error("Invoice not found");
            }
            // 2️⃣ Calculate amount
            const services = invData.service_data || [];
            const totalAmount =
              services.length > 0
                ? services.reduce(
                    (sum, s) => sum + Number(s?.price || 0),
                    0
                  )
                : Number(invData.total || 0);
      
            const { platformFee } = calculateFees(
              totalAmount,
              business?.fee_rate
            );
            const payload = {
              amount: totalAmount,          // dollars
              platformFee,                  // dollars
              providerStripeId: business?.stripe_id,
              email: invData?.info_source_data || "",
              patient_name:invData?.patient_name,
                    business_name:business?.name,
                    appointment_id:invData?.appointment_id,
                    provider_id:invData?.provider_id,
                    serviceType:invData?.service_data?.map(service => service.name).join(", "),
            };
      
            const usingSavedCard =
              prepopulatedCardData &&
              !isEditingCard &&
              prepopulatedCardData.customerId &&
              prepopulatedCardData.paymentMethodId;
      
            if (usingSavedCard) {
              payload.customerId = prepopulatedCardData.customerId;
              payload.paymentMethodId =
                prepopulatedCardData.paymentMethodId;
            }
            
            // 3️⃣ Create payment intent
            
            const response = await fetch(
              `${process.env.REACT_APP_SERVER_URL}/create-payment-intent`,
              {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                  },
                body: JSON.stringify(payload),
              }
            );
      
            if (!response.ok) {
              const err = await response.json().catch(() => null);
              throw new Error(err?.error || "Payment failed");
            }
      
            const result = await response.json();
            // Backend wraps the payload in { success, data, message }; fall back to
            // the top-level object for older/unwrapped responses.
            const paymentData = result?.data || result;

            // 🟢 FLOW A: Saved card → backend already charged
            if (usingSavedCard) {
              if (paymentData.status !== "succeeded") {
                throw new Error("Payment not completed");
              }

              await onPaymentConfirmed(paymentData.paymentIntentId);
              return;
            }

            // 🟢 FLOW B: New card → confirm on frontend
            if (!elements) {
              throw new Error("Stripe Elements not loaded");
            }

            const cardElement = elements.getElement(CardNumberElement);
            if (!cardElement) {
              throw new Error("Card element missing");
            }

            const confirmResult =
              await stripeJs.confirmCardPayment(
                paymentData.clientSecret,
                {
                  payment_method: { card: cardElement },
                }
              );
      
            if (confirmResult.error) {
              throw new Error(confirmResult.error.message);
            }
      
            await onPaymentConfirmed(
              confirmResult.paymentIntent.id
            );
          } catch (err) {
            console.error("Payment error:", err);
            setCardError("");
            toasterNotification(
              "error",
              "",
              err.message || "Payment failed. Please try again."
            );
          } finally {
            setIsMakingPayment(false);
          }
        };
      }, [
        stripeJs,
        elements,
        isAuthorized,
        invoiceId,
        business,
        prepopulatedCardData,
        isEditingCard,
        onPaymentConfirmed,
        token,
        setIsMakingPayment,
      ]);
      
    useImperativeHandle(ref, () => ({
        isCardComplete,
        handlePay: () => handlePayRef.current?.(),
        cardError
    }), [isCardComplete, cardError]);

    const handleCardNumberChange = (event) => {
        setCardNumberComplete(event.complete);
        setCardError(event.error?.message || '');
    };

    const handleCardExpiryChange = (event) => {
        setCardExpiryComplete(event.complete);
        setCardError(event.error?.message || '');
    };

    const handleCardCvcChange = (event) => {
        setCardCvcComplete(event.complete);
        setCardError(event.error?.message || '');
    };

    
    const fetchStripeCardDetails = async (
        customerId,
        preferredPaymentMethodId
      ) => {
        try {
            if (!token) {
                console.error('No authentication token found');
                return null;
            }
            
          const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/stripe/card-details`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
              },
            //credentials: "include", // if auth uses cookies
            body: JSON.stringify({
              customerId,
              preferredPaymentMethodId,
            }),
          });
      
          if (!response.ok) {
            return null;
          }
      
          return await response.json();
        } catch (e) {
          return null;
        }
      };
    // Function to fetch payment details from payment_details table
    const fetchPaymentDetails = async (patientId) => {
        try {
            setIsLoadingCardData(true);
            
            // Validate access token
            if (!token) {
                console.error('No authentication token found');
                setIsLoadingCardData(false);
                return null;
            }

            // Fetch payment details from backend API
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/payment_details`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ patient_id: patientId })
                }
            );

            const result = await response.json();

            let customerId = null;
            let paymentMethodId = null;
            
            if (!response.ok || result.error) {
                const errorMessage = result.error || 'Failed to fetch payment details';
                console.error('Error fetching payment details:', errorMessage);
            } else {
                // Backend returns data in result.data (could be array or single object)
                const paymentDetails = result.data || [];
                const entry = Array.isArray(paymentDetails) && paymentDetails.length > 0 
                    ? paymentDetails[0] 
                    : (paymentDetails && typeof paymentDetails === 'object' ? paymentDetails : null);
                
                if (entry && entry.customer_id) {
                    customerId = entry.customer_id;
                    paymentMethodId = entry.payment_method;
                }
            }

            // Fallback: try to get from patients table if payment_details didn't have data
            if (!customerId) {
                // Fallback: try to get from patients table via backend API
                const patientResponse = await fetch(
                    `${process.env.REACT_APP_SERVER_URL}/api/providers/patients`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        },
                        body: JSON.stringify({ ids: [patientId] })
                    }
                );

                if (patientResponse.ok) {
                    const patientResult = await patientResponse.json();
                    // Backend may return { success, data } or the data directly
                    const patientDataArray = patientResult?.success 
                        ? (Array.isArray(patientResult.data) ? patientResult.data : [patientResult.data])
                        : (Array.isArray(patientResult) ? patientResult : [patientResult]);
                    
                    const patientData = patientDataArray && patientDataArray.length > 0 ? patientDataArray[0] : null;
                    
                    if (patientData?.customer_id) {
                        customerId = patientData.customer_id;
                        paymentMethodId = patientData.payment_method;
                    }
                }
            }
            
            if (!customerId) {
                return null;
            }
            
            const cardDetails = await fetchStripeCardDetails(customerId, paymentMethodId);
            if (cardDetails) {
                return {
                    cardNumber: `**** **** **** ${cardDetails.last4 || ''}`,
                    expiryMonth: cardDetails.exp_month ? String(cardDetails.exp_month).padStart(2, '0') : '',
                    expiryYear: cardDetails.exp_year ? String(cardDetails.exp_year).slice(-2) : '',
                    cvv: '***',
                    customerId: customerId,
                    paymentMethodId: paymentMethodId
                };
            }
            return null;
        } catch (e) {
            console.error('Error fetching payment details:', e);
            return null;
        } finally {
            setIsLoadingCardData(false);
        }
    };

    // Load payment details when component mounts or patientId changes
    useEffect(() => {
        if (patientId) {
            fetchPaymentDetails(patientId).then((cardData) => {
                if (cardData) {
                    setPrepopulatedCardData(cardData);
                }
            });
        }
    }, [patientId]);

    return (
        <div className="flex flex-col h-full font-[Poppins]">
            <style>
                {`
                    .stripe-input-container .StripeElement {
                        background-color: inherit !important;
                        color: #000000 !important;
                    }
                    .stripe-input-container .StripeElement--focus {
                        background-color: white !important;
                    }
                    .stripe-input-container .StripeElement--invalid {
                        background-color: inherit !important;
                    }
                    .stripe-input-container .StripeElement::placeholder,
                    .stripe-input-container input::placeholder {
                        color: #9ca3af !important;
                    }
                `}
            </style>

            {/* Card details */}
            <div style={{ marginBottom: '60px' }}>
                <FormHeading>Card Details</FormHeading>

                <div className="space-y-4">
                    {isLoadingCardData ? (
                        <div className="text-center py-4">
                            <div className="text-gray-500">Loading saved card details...</div>
                        </div>
                    ) : prepopulatedCardData && !isEditingCard ? (
                        // Show saved card details as actual values with edit option
                        <>
                            {/* Card Number */}
                            <div>
                                <div className="border border-orange-200 rounded-lg bg-[#f4efeb] p-3 flex justify-between items-center">
                                    <span className="text-gray-900 font-medium">{prepopulatedCardData.cardNumber}</span>
                                    <button 
                                        type="button"
                                        onClick={() => setIsEditingCard(true)}
                                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                    >
                                        Edit
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                {/* Expiration Date */}
                                <div>
                                    <div className="border border-gray-200 rounded-lg p-3 bg-[#f4efeb]">
                                        <span className="text-gray-900 font-medium">**/**</span>
                                    </div>
                                </div>

                                {/* CVV */}
                                <div>
                                    <div className="border border-gray-200 rounded-lg bg-[#f4efeb] p-3">
                                        <span className="text-gray-900 font-medium">{prepopulatedCardData.cvv}</span>
                                    </div>
                                </div>
                            </div>
                            
                        </>
                    ) : (
                        // Show Stripe input fields for editing or when no saved data
                        <>
                            {/* Card Number */}
                            <div>
                                <div className="stripe-input-container" style={{ 
                                    border: '1px solid #cbc1b7', 
                                    borderRadius: '8px', 
                                    backgroundColor: '#f5f1ed', 
                                    padding: '9.5px 14px 9.5px 20px',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.borderColor = '#ff5f39';
                                    e.target.style.backgroundColor = 'white';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.borderColor = '#cbc1b7';
                                    e.target.style.backgroundColor = '#f5f1ed';
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#ff5f39';
                                    e.target.style.backgroundColor = 'white';
                                    e.target.style.boxShadow = '0 0 0 2px rgba(255, 95, 57, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#cbc1b7';
                                    e.target.style.backgroundColor = '#f5f1ed';
                                    e.target.style.boxShadow = 'none';
                                }}>
                                    <CardNumberElement
                                        options={{
                                            style: {
                                                base: {
                                                    fontSize: '16px',
                                                    color: '#1f2937',
                                                    '::placeholder': { color: '#9ca3af' },
                                                },
                                            },
                                            placeholder: 'Card number'
                                        }}
                                        onChange={handleCardNumberChange}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                {/* Expiration Date */}
                                <div>
                                    <div style={{ 
                                        border: '1px solid #cbc1b7', 
                                        borderRadius: '8px', 
                                        backgroundColor: '#f5f1ed', 
                                        padding: '9.5px 14px 9.5px 20px',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.borderColor = '#ff5f39';
                                        e.target.style.backgroundColor = 'white';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.borderColor = '#cbc1b7';
                                        e.target.style.backgroundColor = '#f5f1ed';
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#ff5f39';
                                        e.target.style.backgroundColor = 'white';
                                        e.target.style.boxShadow = '0 0 0 2px rgba(255, 95, 57, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#cbc1b7';
                                        e.target.style.backgroundColor = '#f5f1ed';
                                        e.target.style.boxShadow = 'none';
                                    }}>
                                        <CardExpiryElement
                                            options={{
                                            style: {
                                                base: {
                                                    fontSize: '16px',
                                                    color: '#1f2937',
                                                    '::placeholder': { color: '#9ca3af' },
                                                },
                                            },
                                            placeholder: 'MM / YY'
                                            }}
                                            onChange={handleCardExpiryChange}
                                        />
                                    </div>
                                </div>

                                {/* CVV */}
                                <div>
                                    <div style={{ 
                                        border: '1px solid #cbc1b7', 
                                        borderRadius: '8px', 
                                        backgroundColor: '#f5f1ed', 
                                        padding: '9.5px 14px 9.5px 20px',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.borderColor = '#ff5f39';
                                        e.target.style.backgroundColor = 'white';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.borderColor = '#cbc1b7';
                                        e.target.style.backgroundColor = '#f5f1ed';
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#ff5f39';
                                        e.target.style.backgroundColor = 'white';
                                        e.target.style.boxShadow = '0 0 0 2px rgba(255, 95, 57, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#cbc1b7';
                                        e.target.style.backgroundColor = '#f5f1ed';
                                        e.target.style.boxShadow = 'none';
                                    }}>
                                        <CardCvcElement
                                            options={{
                                            style: {
                                                base: {
                                                    fontSize: '16px',
                                                    color: '#1f2937',
                                                    '::placeholder': { color: '#9ca3af' },
                                                },
                                            },
                                            placeholder: 'CVC'
                                            }}
                                            onChange={handleCardCvcChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {cardError && <div className="text-red-500 text-sm">{cardError}</div>}
                </div>
            </div>

            {/* Authorization */}
            <div className="mb-8 flex-grow">
                <div className="text-base font-bold text-gray-800 mb-2">
                    By checking the box and tapping pay, I hereby:
                </div>
                <div className="w-full h-px mb-4" style={{ backgroundColor: 'rgba(196, 196, 196, 1)' }}></div>
                <div className="flex gap-2" style={{ alignItems: 'center' }}>
                    <div 
                        className="cursor-pointer"
                        onClick={() => onAuthorizationChange && onAuthorizationChange(!isAuthorized)}
                        style={{
                            width: 16,
                            height: 16,
                            borderRadius: '4px',
                            border: isAuthorized ? '1px solid #75BD2F' : '1px solid #cbc1b7',
                            backgroundColor: isAuthorized ? '#75BD2F' : '#f5f1ed',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: 0,
                            transition: 'all 0.3s ease',
                            flexShrink: 0
                        }}
                    >
                        {isAuthorized && (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="white" stroke="white" strokeWidth="1.5" />
                            </svg>
                        )}
                    </div>
                    <div 
                        onClick={() => onAuthorizationChange && onAuthorizationChange(!isAuthorized)}
                        style={{ fontSize: '14px', color: '#3E3E4A', fontFamily: 'Poppins, sans-serif', fontWeight: '500', margin: 0, cursor: 'pointer', lineHeight: '1.5' }}
                    >
                        Confirm the patient verbally authorized me to enter their card details for this specific invoice.
                    </div>
                </div>
            </div>
        </div>
    );
});

export default ManualPaymentForm;
